import React from 'react';
import propType from 'prop-types';
import { Theme } from '../interfaces/IStepperForm';
import { createTheme, ThemeProvider } from '@mui/material';

const Themes: React.FC<Theme> = ({ children }) => {
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });

  const customTheme = createTheme({
    ...theme,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: '12px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontFamily: 'GeneralSans !important',
            minWidth: '120px',
            fontSize: '16px',
            height: '48px',
            fontWeight: '500',
            borderRadius: '8px',
            textWrap: 'nowrap',
            boxShadow: 'none',
            '@media (max-width: 1440px)': {
              minWidth: '100px',
              fontSize: '12px',
              height: '38px',
              fontWeight: '400',
            },
            [theme.breakpoints.down('md')]: {
              height: '44px',
            },
          },
        },
        variants: [
          {
            props: { variant: 'contained' },
            style: {
              background: '#604797',
              color: '#fff',
              padding: '12px',
              ':hover': {
                background: '#4B3776',
              },
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              background: 'white',
              color: '#333333',
              padding: '12px',
              border: '1px solid #D1C8E3',
            },
          },
          {
            props: { variant: 'outlinedII' },
            style: {
              background: 'white',
              color: '#604797',
              padding: '8px 12px',
              fontWeight: '600',
              fontSize: '16px',
              border: '1px solid #604797',
              ':hover': {
                background: '#D1C8E3',
              },
            },
          },
          {
            props: { variant: 'containedII' },
            style: {
              background: '#F0EDF6',
              color: '#604797',
              padding: '12px',
              ':hover': {
                background: '#F0EDF6',
              },
            },
          },
          {
            props: { variant: 'containedIII' },
            style: {
              background: '#FBEAEA',
              color: '#962121',
              padding: '12px',
              ':hover': {
                background: '#FBEAEA',
              },
            },
          },
          {
            props: { variant: 'containedIV' },
            style: {
              background: '#EBF4EC',
              color: '#28652B',
              padding: '12px',
              ':hover': {
                background: '#EBF4EC',
              },
            },
          },
          {
            props: { variant: 'text' },
            style: {
              color: '#6A4EA6',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '0px',
              padding: '4px',
              [`${theme.breakpoints.down('md')}`]: {
                fontSize: '14px',
                fontWeight: '500',
              },
            },
          },
        ],
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: 'GeneralSans',
            letterSpacing: '0px',
          },
        },
        variants: [
          {
            props: { variant: 'extralargebold' },
            style: {
              fontSize: '24px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'extralargemedium' },
            style: {
              fontSize: '24px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'extralargethin' },
            style: {
              fontSize: '24px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'largebold' },
            style: {
              fontSize: '20px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'largemedium' },
            style: {
              fontSize: '20px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'largethin' },
            style: {
              fontSize: '20px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'mediumbold' },
            style: {
              fontSize: '16px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'medium' },
            style: {
              fontSize: '16px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'mediumthin' },
            style: {
              fontSize: '16px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'smallbold' },
            style: {
              fontSize: '14px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'smallmedium' },
            style: {
              fontSize: '14px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'smallthin' },
            style: {
              fontSize: '14px',
              fontWeight: '400',
            },
          },
          {
            props: { variant: 'extrasmallbold' },
            style: {
              fontSize: '12px',
              fontWeight: '600',
            },
          },
          {
            props: { variant: 'extrasmallmedium' },
            style: {
              fontSize: '12px',
              fontWeight: '500',
            },
          },
          {
            props: { variant: 'extrasmallthin' },
            style: {
              fontSize: '12px',
              fontWeight: '400',
            },
          },
        ],
      },
    },
  });
  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
};

Themes.propTypes = {
  children: propType.node.isRequired,
};

export default Themes;
